import { Button, Modal, Select, TextInput } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Loader from '../Common/Loader';
import { useNavigate } from 'react-router-dom';
import { Asset } from '../../Models/Asset';
import {
  fetchAssetStatuses,
  fetchAssetTypes,
} from '../../Actions/CommonFields';
import { useAppDispatch, useAppSelector } from '../../Reducers/store';
import AssetApi from '../../Api/AssetApi';
import { onlyNumberInput } from '../../Utils';

export default function AddAssetModal({
  show,
  setShow,
  selectedAsset,
  getAssetList,
  setselectedAsset,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAsset: Asset | null;
  getAssetList: () => Promise<void> | undefined;
  setselectedAsset: (value: React.SetStateAction<Asset | null>) => void;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { assetStatuses, assetTypes } = useAppSelector(
    (state) => state.commonFields
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [formData, setFormData] = useState<Asset>({
    id: 0,
    name: null,
    number: null,
    dateCreated: null,
    statusId: 1,
    typeId: 1,
    vin: null,
    color: null,
    originalMileage: null,
    currentMileage: null,
    currentMileageDate: null,
    year: null,
    make: null,
    model: null,
    purchasePrice: null,

    assetQuantities: [],
    status: null,
    type: null,
  });

  const handleSetInitialValues = () => {
    const values = selectedAsset
      ? {
          ...selectedAsset,
        }
      : {
          id: 0,
          name: null,
          number: null,
          dateCreated: null,
          statusId: 1,
          typeId: 1,
          vin: null,
          color: null,
          originalMileage: null,
          currentMileage: null,
          currentMileageDate: null,
          year: null,
          make: null,
          model: null,
          purchasePrice: null,

          status: null,
          type: null,
        };
    setFormData(values as Asset);
  };

  useEffect(() => {
    handleSetInitialValues();
  }, [selectedAsset]);

  const handleResetFormValues = () => {
    const values = {
      id: 0,
      name: null,
      number: null,
      dateCreated: null,
      statusId: 1,
      typeId: 1,
      vin: null,
      color: null,
      originalMileage: null,
      currentMileage: null,
      currentMileageDate: null,
      year: null,
      make: null,
      model: null,
      purchasePrice: null,

      status: null,
      type: null,
    };
    setFormData(values as Asset);
    setButtonDisabled(false);
  };

  const getReferences = async () => {
    if (!assetStatuses.length || !assetTypes.length) {
      await Promise.all([
        dispatch(fetchAssetStatuses()),
        dispatch(fetchAssetTypes()),
      ]);
    }
  };

  useEffect(() => {
    if (show === true) {
      getReferences();
    }
  }, [show]);

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    setButtonDisabled(true);
    setIsLoading(true);
    return AssetApi.addUpdateAsset(formData)
      .then((res) => {
        if (res.data.success) {
          toast.success('Success');
          setIsLoading(false);
          setButtonDisabled(false);
          getAssetList();
          onCloseModal();
        } else {
          toast.error(res.data.message);
          setIsLoading(false);
          setButtonDisabled(false);
        }
      })
      .catch((err) => {
        toast.error('Error: ' + err);
        setIsLoading(false);
        setButtonDisabled(false);
      });
  };
  function onCloseModal() {
    handleResetFormValues();
    setselectedAsset(null);
    setShow(false);
  }
  return (
    <Modal show={show} size='4xl' onClose={onCloseModal} popup>
      <Modal.Header></Modal.Header>
      <Modal.Body>
        <div className='w-full'>
          <div>
            <div className='container mx-auto p-2'>
              <div className='md:flex no-wrap md:-mx-2 '>
                <div className='w-full'>
                  <div className='bg-white'>
                    <div className='flex items-center space-x-2 font-bold text-black leading-8'>
                      <span className='tracking-wide uppercase text-3xl'>
                        Asset Information
                      </span>
                    </div>
                    <hr className='bg-gray-300 h-[5px]'></hr>
                    <form onSubmit={onSubmit}>
                      <div className='text-gray-500'>
                        <div className='grid grid-cols-[30%_70%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Status:
                            </div>
                          </div>
                          <div className='w-full'>
                            <Select
                              id='statusId'
                              name='statusId'
                              onChange={handleChangeSelect}
                              value={formData.statusId ?? ''}
                              sizing='md'
                            >
                              {assetStatuses.map((obj) => (
                                <option key={obj.id} value={obj.id}>
                                  {obj.name}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[30%_70%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Type:
                            </div>
                          </div>
                          <div className='w-full'>
                            <Select
                              id='typeId'
                              name='typeId'
                              onChange={handleChangeSelect}
                              value={formData.typeId ?? ''}
                              sizing='md'
                            >
                              {assetTypes.map((obj) => (
                                <option key={obj.id} value={obj.id}>
                                  {obj.name}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[30%_70%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Name:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='name'
                              name='name'
                              type='text'
                              placeholder='Name...'
                              onChange={handleChangeInput}
                              value={formData.name ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[30%_70%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Number:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='number'
                              name='number'
                              type='text'
                              placeholder='Number...'
                              onChange={handleChangeInput}
                              value={formData.number ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[30%_70%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Current Mileage:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='currentMileage'
                              name='currentMileage'
                              type='text'
                              placeholder='Current Mileage...'
                              onChange={handleChangeInput}
                              onKeyDown={(event) => {
                                if (onlyNumberInput(event)) {
                                  event.preventDefault();
                                }
                              }}
                              value={formData.currentMileage ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                      </div>
                      <div className='flex justify-end pt-2'>
                        <Button
                          disabled={buttonDisabled}
                          type='submit'
                          className='buttonMain'
                        >
                          Save
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
